// React Dependencies
import React, { useState } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Dialog, DialogActions, DialogContent, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { Delete, ShowChart, CardMembership } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';

// Internal Dependencies
import HeadingAndSubText from './HeadingAndSubText';
import MIconButton from '../../../../../components/MIconButton';
import MBadge from '../../../../../components/MBadge';
import Colours from '../../../../../styles/colours';
import { GiftType, Status, Url } from '../../../../../common/constants';

import AuthorizedIssuers from './AuthorizedIssuers';
import ParticipatingOutlets from './ParticipatingOutlets';
import LoyaltyCardList from './LoyaltyCardList';
import MText from '../../../../../components/Common/MText';
import MButton from '../../../../../components/MButton';
import { IsTrue } from '../../../../../common/checks';

const useStyles = makeStyles({
  container: {
    display:         'flex',
    flexDirection:   'row',
    justifyContent:  'space-between',
    backgroundColor: '#F2F2F2',
    width:           '82%',
    borderRadius:    '10px',
    paddingLeft:     '1.25rem',
    paddingRight:    '1.25rem',
    marginBottom:    '0.75rem',

    '@media (max-width: 1490px)': {
      flexWrap: 'wrap',
    },
    '@media (max-width: 600px)': {
      width:        '100%',
      paddingLeft:  '0.75rem',
      paddingRight: '0.75rem',
    },
  },
  subContainer: {
    paddingTop:    '1.5rem',
    paddingBottom: '1.5rem',

    '@media (max-width: 1149px)': {
      flexWrap:  'wrap',
      margin:    '0 !important',
      marginTop: '1rem',
    },

  },
  actionContainer: {
    paddingTop:    '1.5rem',
    paddingBottom: '1.5rem',
    borderLeft:    '2px solid #FEF6F6',
    paddingLeft:   '1.25rem',

    '@media (max-width: 1490px)': {
      paddingTop: '0',
      border:     'none',
    },
  },
  subContainerAction: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '0.8rem',
  },
  mobileActionIconBtns: {
    width:   '30px !important',
    padding: '18px !important',
  },
});

const MODAL_TYPE = {
  TERMS:       'T&Cs',
  AUTHORIZED:  'Authorized Issuers',
  PARTICIPANT: 'Participating Outlets',
  DELETE:      'Delete',
  LOYALTY:     'Linked Loyalty Cards',
};

const GiftCard = ({ gift, pageId, onDeleteGift }) => {
  const classes = useStyles();
  const history = useHistory();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => setIsModalOpen(false);
  const viewModalTermsAndConditionsHandler = () => {
    setModalType(MODAL_TYPE.TERMS);
    openModal();
  };

  const editHandler = () => history.push(Url.Gift.replace(':pageId', pageId).replace(':giftId', gift.id));

  const openModalHandler = (type) => {
    setModalType(type);
    openModal();
  };

  const formatDate = (date) => {
    const newDate = new Date(date);
    return moment(newDate).format('YYYY-MM-DD');
  };

  const handleLoyaltyCardClick = (cardId) => {
    closeModal();
    window.open(`/page/${pageId}/loyaltyCard/${cardId}`, '_blank');
  };

  const isLinkedToLoyaltyCard = IsTrue(gift.isLinkedToLoyaltyCard);

  return (
    <div className={classes.container} aria-hidden="true">
      <div className={`d-flex flex-row ${classes.subContainer}`} style={{ gap: '0.5rem' }}>
        <div className="d-flex flex-column" style={{ gap: '0.5rem' }}>
          <div
            className="d-flex justify-content-center align-items-center py-4"
            style={{ width: '14.6rem', height: '7.9rem', backgroundColor: '#D9D9D9', textAlign: 'center', borderRadius: '4px' }}
          >
            { !_.isEmpty(gift.images) ? (
              <img
                src={gift.images[0].display}
                style={{ width: '100%', height: '7.9rem', objectFit: 'cover', borderRadius: '4px' }}
                alt={gift.label}
              />
            ) : <MText text="No image" />}
          </div>

          <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
            {
              gift.pauseIssuance
                ? <MBadge text="Issuance Paused" colour={Colours.Orange} textColour={Colours.White} style={{ height: 30 }} />
                : (
                  <MBadge
                    text={gift.status}
                    colour={
                      gift.status === Status.Draft ? Colours.Gray
                        : gift.status === Status.Activated ? Colours.Green
                          : gift.status === Status.Deactivated ? Colours.Red
                            : Colours.Black
                    }
                    textColour={Colours.White}
                    style={{ height: 30 }}
                  />
                )
            }
            { !_.isEmpty(gift.terms)
              && (
                <MBadge
                  text="View T&Cs"
                  textColour={Colours.White}
                  colour={Colours.Gray5}
                  onClick={viewModalTermsAndConditionsHandler}
                  style={{ height: 30 }}
                />
              )}
          </div>

          {/** For mobile view UI */}
          <div className="d-md-none mt-3">
            <MText text={`${gift.type} Program ID: ${gift.id}`} bold />

            <div className="mt-2 d-flex" style={{ gap: '0.5rem' }}>
              {/* <MButton
                text="Edit"
                textClassName="ml-1"
                icon={() => <Edit fontSize="small" />}
                onClick={editHandler}
                style={{ borderRadius: 20 }}
              /> */}
              {
                [Status.Activated, Status.Deactivated].includes(gift.status) && (
                  <MButton
                    text={`${gift.type === GiftType.Voucher ? 'Voucher' : 'Coupon'} Tracking`}
                    textClassName="ml-1"
                    icon={() => <ShowChart fontSize="small" className="ml-1" />}
                    onClick={() => history.push(Url.CouponVoucherTracking.replace(':pageId', pageId).replace(':giftId', gift.id))}
                    style={{ borderRadius: 20 }}
                  />
                )
              }
              {/* <MIconButton
                className={classes.mobileActionIconBtns}
                onClick={() => openModalHandler(MODAL_TYPE.AUTHORIZED)}
                icon={() => <GroupOutlined fontSize="small" className="ml-1" />}
              />
              <MIconButton
                className={classes.mobileActionIconBtns}
                onClick={() => openModalHandler(MODAL_TYPE.PARTICIPANT)}
                icon={() => <VisibilityOutlined fontSize="small" className="ml-1" />}
              />
              <MIconButton className={classes.mobileActionIconBtns} icon={() => <Storefront fontSize="small" className="ml-1" />} /> */}

              {
                gift.status === Status.Draft && (
                  <div className="d-flex flex-column w-50" style={{ rowGap: 8 }}>
                    <Tooltip title={isLinkedToLoyaltyCard ? `Cannot delete ${gift.type} because it is linked to a loyalty program` : ''} arrow>
                      <span style={{ cursor: isLinkedToLoyaltyCard ? 'not-allowed' : 'pointer' }}>
                        <MButton
                          text="Delete"
                          className="w-100"
                          textClassName="ml-1"
                          icon={() => <Delete fontSize="small" />}
                          onClick={() => openModalHandler(MODAL_TYPE.DELETE)}
                          disabled={isLinkedToLoyaltyCard}
                          style={{ borderRadius: 20 }}
                        />
                      </span>
                    </Tooltip>
                  </div>
                )
              }

              {
                isLinkedToLoyaltyCard && (
                  <MButton
                    text="Linked Loyalty Card"
                    textClassName="ml-1"
                    icon={() => <CardMembership fontSize="small" />}
                    onClick={() => openModalHandler(MODAL_TYPE.LOYALTY)}
                    style={{ borderRadius: 20 }}
                  />
                )
              }
            </div>
          </div>
        </div>

        {/** Displayed only in tablet and desktop view */}
        <div className="d-none d-md-flex flex-column" style={{ gap: '0.5rem' }}>
          <HeadingAndSubText heading="Name:" text={gift.label} />
          <HeadingAndSubText heading={`${gift.type} Program ID:`} text={gift.id} />

          <div className="d-flex flex-row" style={{ gap: '1rem' }}>
            <HeadingAndSubText heading="Start Date:" text={!_.isEmpty(gift.startDate) ? formatDate(gift.startDate) : '-'} />
            <HeadingAndSubText heading="Expiry Date:" text={!_.isEmpty(gift.endDate) ? formatDate(gift.endDate) : '-'} />
            <HeadingAndSubText heading="Extended Expiry Date:" text={!_.isEmpty(gift.extendedEndDate) ? formatDate(gift.extendedEndDate) : '-'} />
          </div>
        </div>
      </div>

      {/** For tablet and desktop view UI */}
      <div
        className={`d-none d-md-flex flex-column ${classes.actionContainer}`}
        style={{ flexWrap: 'wrap', gap: '0.8rem' }}
      >
        <MText text="Actions:" size={12} />

        <div className={`d-flex flex-column ${classes.subContainerAction}`}>
          {/* <MIconButton onClick={editHandler} icon={() => <Edit fontSize="small" className="ml-1" />} text="Edit" /> */}

          {
            [Status.Activated, Status.Deactivated].includes(gift.status) && (
              <MIconButton
                onClick={() => history.push(Url.CouponVoucherTracking.replace(':pageId', pageId).replace(':giftId', gift.id))}
                icon={() => <ShowChart fontSize="small" className="ml-1" />}
                text={`${gift.type === GiftType.Voucher ? 'Voucher' : 'Coupon'} Tracking`}
              />
            )
          }

          {/** Temp hide these features */}
          {/* <MIconButton
            onClick={() => openModalHandler(MODAL_TYPE.AUTHORIZED)}
            icon={() => <GroupOutlined fontSize="small" className="ml-1" />}
            text="Authorized Issuers"
          /> */}
          {
            // Temporarily hide viewing participating outlets for Gift because we have a case
            // where the Gift can be used at all outlets without specifying the POs.
            // E.g. Suu Kee Coffee, where they are using 3rd party POS system.
          }
          {/* <MIconButton
            onClick={() => openModalHandler(MODAL_TYPE.PARTICIPANT)}
            icon={() => <VisibilityOutlined fontSize="small" className="ml-1" />}
            text="Participating Outlets"
          /> */}
          {/* <MIconButton icon={() => <Storefront fontSize="small" className="ml-1" />} text="Voucher Tracking" /> */}
          {
            gift.status === Status.Draft && (
              <>
                <Tooltip title={isLinkedToLoyaltyCard ? `Cannot delete ${gift.type} because it is linked to a loyalty program` : ''} arrow>
                  <span style={{ cursor: isLinkedToLoyaltyCard ? 'not-allowed' : 'pointer' }}>
                    <MIconButton
                      onClick={() => openModalHandler(MODAL_TYPE.DELETE)}
                      icon={() => <Delete fontSize="small" className="ml-1" />}
                      text="Delete"
                      disabled={isLinkedToLoyaltyCard}
                      textColour={isLinkedToLoyaltyCard ? null : Colours.Black}
                    />
                  </span>
                </Tooltip>
              </>
            )
          }

          {
            isLinkedToLoyaltyCard && (
              <MIconButton
                onClick={() => openModalHandler(MODAL_TYPE.LOYALTY)}
                icon={() => (
                  <CardMembership
                    htmlColor={Colours.Black}
                    fontSize="small"
                    className="ml-1"
                  />
                )}
                text="Linked Loyalty Card"
              />
            )
          }
        </div>
      </div>

      <Dialog
        open={isModalOpen}
        fullWidth
        maxWidth={
          modalType === MODAL_TYPE.DELETE || modalType === MODAL_TYPE.LOYALTY ? 'sm' : 'lg'
        }
        onClose={closeModal}
      >
        <DialogContent className="mt-2">
          { modalType === MODAL_TYPE.TERMS && (
            <div style={{ height: '50vh' }}>
              <Typography variant="body1" className="font-weight-bold mb-3">Terms & Conditions</Typography>
              <Typography variant="subtitle1" style={{ fontSize: '14px !important' }}>{gift.terms}</Typography>
            </div>
          )}
          {
            modalType === MODAL_TYPE.AUTHORIZED && <AuthorizedIssuers gift={gift} />
          }
          {
            modalType === MODAL_TYPE.PARTICIPANT && <ParticipatingOutlets gift={gift} />
          }
          {
            modalType === MODAL_TYPE.LOYALTY && <LoyaltyCardList gift={gift} handleLoyaltyCardClick={handleLoyaltyCardClick} />
          }
          {
            modalType === MODAL_TYPE.DELETE && (
              <>
                <MText
                  className="mb-2"
                  size={14}
                  bold
                  text={`Are you sure you want to delete this ${gift.type}?`}
                />
                <MText
                  size={13}
                  text={`ID: ${gift.id}`}
                />
              </>
            )
          }
        </DialogContent>

        <DialogActions className="pb-3 pr-3">
          {modalType === MODAL_TYPE.DELETE && (
            <MButton
              className="mr-4 px-4"
              onClick={onDeleteGift}
              // colour={Colours.Secondary}
              textColour={Colours.Secondary}
              text="Delete"
            />
          )}
          <MButton
            className="px-4"
            onClick={() => closeModal()}
            textColour={Colours.Black}
            text={modalType === MODAL_TYPE.DELETE ? 'Cancel' : 'Close'}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

GiftCard.propTypes = {
  onDeleteGift: PropTypes.func.isRequired,
  gift:         PropTypes.shape({
    label:                 PropTypes.string,
    id:                    PropTypes.string,
    type:                  PropTypes.string,
    startDate:             PropTypes.oneOfType([PropTypes.string, PropTypes.shape(Object)]),
    endDate:               PropTypes.oneOfType([PropTypes.string, PropTypes.shape(Object)]),
    extendedEndDate:       PropTypes.oneOfType([PropTypes.string, PropTypes.shape(Object)]),
    status:                PropTypes.string,
    terms:                 PropTypes.string,
    images:                PropTypes.arrayOf(PropTypes.shape(Object)),
    pauseIssuance:         PropTypes.bool,
    isLinkedToLoyaltyCard: PropTypes.bool,
    loyaltyCards:          PropTypes.arrayOf(
      PropTypes.shape({
        id:   PropTypes.string,
        name: PropTypes.string,
      }),
    ),
  }).isRequired,
  pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default GiftCard;
